/**
 * トロフィー棚。
 *
 * - 上段：県メダルの一覧（地方ごと。銅・銀・金・白金で色が変わる）
 * - 下段：トロフィー。色は難しさ（銅 → 銀 → 金 → 白金）
 * - 未獲得のものも条件と進み具合（12 / 30）を見せる。何をすれば取れるかが分かることが大事
 *
 * DOM は自前で組み立てる。県えらびの画面（Phaser を起動しない）からも開けるようにするため。
 */

import { AREAS } from '../game/config/regions';
import type { TrophyBook, TrophySnapshot } from '../meta/progress';
import {
  GROUP_LABEL,
  MEDAL_LABEL,
  TIER_LABEL,
  medalOf,
  medalRequirement,
  type TrophyGroup,
  type TrophyStatus,
  type TrophyTier,
} from '../meta/trophies';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const GROUP_ORDER: TrophyGroup[] = [
  'start',
  'step',
  'daily',
  'medal',
  'area',
  'category',
  'dex',
  'tour',
  'play',
];
const TIERS: TrophyTier[] = ['bronze', 'silver', 'gold', 'platinum'];

export class TrophyShelf {
  private readonly root: HTMLElement;
  private readonly body: HTMLElement;
  private readonly countEl: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;

  /** 閉じたときに呼ばれる。 */
  onClose?: () => void;

  constructor(private readonly book: TrophyBook) {
    this.root = el('div', 'modal trophy-modal');
    this.root.dataset.visible = 'false';
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-modal', 'true');
    this.root.setAttribute('aria-labelledby', 'trophy-title');

    const backdrop = el('div', 'modal__backdrop');
    backdrop.addEventListener('click', () => this.hide());

    const card = el('div', 'modal__card trophy-card');
    const head = el('div', 'modal__head');
    const title = el('h2', 'modal__title', 'トロフィー棚');
    title.id = 'trophy-title';
    this.countEl = el('span', 'panel__hint');
    title.append(' ', this.countEl);
    this.closeBtn = el('button', 'btn btn--icon');
    this.closeBtn.type = 'button';
    this.closeBtn.setAttribute('aria-label', 'トロフィー棚を閉じる');
    this.closeBtn.append(el('span', undefined, '✕'));
    this.closeBtn.addEventListener('click', () => this.hide());
    head.append(title, this.closeBtn);

    this.body = el('div', 'modal__body trophy-body');
    card.append(head, this.body);
    this.root.append(backdrop, card);
    document.body.append(this.root);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.hide();
    });
  }

  open(): void {
    const snapshot = this.book.sync();
    this.render(snapshot);
    this.root.dataset.visible = 'true';
    this.closeBtn.focus();
    // 開いた時点で新着は見たことにする（NEW 表示は今回の描画には残る）
    this.book.markAllSeen();
  }

  hide(): void {
    if (!this.isOpen()) return;
    this.root.dataset.visible = 'false';
    this.onClose?.();
  }

  isOpen(): boolean {
    return this.root.dataset.visible === 'true';
  }

  private render(snapshot: TrophySnapshot): void {
    const achieved = snapshot.statuses.filter((s) => s.achieved).length;
    this.countEl.textContent = `${achieved} / ${snapshot.statuses.length}`;

    this.body.replaceChildren(
      this.renderTierSummary(snapshot.statuses),
      this.renderMedals(snapshot),
      ...GROUP_ORDER.map((group) =>
        this.renderGroup(
          group,
          snapshot.statuses.filter((s) => s.trophy.group === group),
          snapshot.unseen,
        ),
      ),
    );
  }

  private renderTierSummary(statuses: readonly TrophyStatus[]): HTMLElement {
    const row = el('div', 'trophy-tiers');
    for (const tier of TIERS) {
      const inTier = statuses.filter((s) => s.trophy.tier === tier);
      const got = inTier.filter((s) => s.achieved).length;
      const chip = el('span', 'trophy-tier-chip');
      chip.dataset.tier = tier;
      chip.textContent = `${TIER_LABEL[tier]} ${got}/${inTier.length}`;
      row.append(chip);
    }
    return row;
  }

  private renderMedals(snapshot: TrophySnapshot): HTMLElement {
    const section = el('section', 'trophy-section');
    const maxLevel = snapshot.progress.prefs[0]?.maxLevel ?? 12;
    const req = medalRequirement(maxLevel);
    section.append(el('h3', 'trophy-section__title', '県メダル'));
    const legend = el('p', 'trophy-legend');
    legend.textContent = `銅 ${req.bronze}／銀 ${req.silver}／金 ${req.gold}／白金 ${req.master}`;
    section.append(legend);

    for (const area of AREAS) {
      // 並びは都道府県コード順（regions.ts の表の順）
      const prefs = area.regionIds.flatMap((id) =>
        snapshot.progress.prefs.filter((p) => p.regionId === id),
      );
      if (prefs.length === 0) continue;
      const row = el('div', 'medal-row');
      row.append(el('span', 'medal-row__area', area.name));
      const tiles = el('div', 'medal-row__tiles');
      for (const p of prefs) {
        const medal = medalOf(p);
        const tile = el('span', 'medal-tile', p.regionName.replace(/[都府県]$/, ''));
        tile.dataset.medal = medal;
        // 色だけに頼らない：メダルの字も添える
        if (medal !== 'none') tile.append(el('b', 'medal-tile__mark', MEDAL_LABEL[medal]));
        tile.title = `${p.regionName}：${MEDAL_LABEL[medal]}（最高 Lv${p.reached}）`;
        tile.setAttribute('aria-label', tile.title);
        tiles.append(tile);
      }
      row.append(tiles);
      section.append(row);
    }
    return section;
  }

  private renderGroup(
    group: TrophyGroup,
    statuses: readonly TrophyStatus[],
    unseen: ReadonlySet<string>,
  ): HTMLElement {
    const section = el('section', 'trophy-section');
    const got = statuses.filter((s) => s.achieved).length;
    const heading = el('h3', 'trophy-section__title', GROUP_LABEL[group]);
    heading.append(el('span', 'trophy-section__count', ` ${got} / ${statuses.length}`));
    section.append(heading);

    const grid = el('div', 'trophy-grid');
    // 取ったもの → 近いもの（進み具合の割合が高い順）
    const sorted = [...statuses].sort((a, b) => {
      if (a.achieved !== b.achieved) return a.achieved ? -1 : 1;
      return b.current / b.target - a.current / a.target;
    });
    for (const status of sorted) grid.append(this.renderCard(status, unseen.has(status.trophy.id)));
    section.append(grid);
    return section;
  }

  private renderCard(status: TrophyStatus, isNew: boolean): HTMLElement {
    const { trophy } = status;
    const card = el('div', 'trophy');
    card.dataset.tier = trophy.tier;
    card.dataset.achieved = String(status.achieved);

    const icon = el('span', 'trophy__icon', trophy.icon);
    icon.setAttribute('aria-hidden', 'true');
    const name = el('span', 'trophy__name', trophy.name);
    const tier = el('span', 'trophy__tier', TIER_LABEL[trophy.tier]);
    const req = el('span', 'trophy__req', trophy.requirement);
    card.append(icon, name, tier, req);

    if (!status.achieved) {
      const bar = el('span', 'trophy__bar');
      const fill = el('span', 'trophy__fill');
      fill.style.width = `${Math.round((status.current / status.target) * 100)}%`;
      bar.append(fill);
      card.append(bar, el('span', 'trophy__count', `${status.current} / ${status.target}`));
    }
    if (isNew) card.append(el('span', 'trophy__new', 'NEW'));
    card.setAttribute(
      'aria-label',
      `${trophy.name}（${TIER_LABEL[trophy.tier]}）${status.achieved ? '獲得済み' : `未獲得 ${status.current}/${status.target}`}。${trophy.requirement}`,
    );
    return card;
  }
}

/** トロフィーを開くボタンに新着の数を出す。 */
export function renderTrophyBadge(button: HTMLElement, count: number): void {
  let badge = button.querySelector<HTMLElement>('.trophy-badge');
  if (count <= 0) {
    badge?.remove();
    return;
  }
  if (!badge) {
    badge = el('span', 'trophy-badge');
    button.append(badge);
  }
  badge.textContent = String(count);
}
